/**
 * The assessment stream — the indexed sequence of phrases that actually count
 * toward complexity adjustment.
 *
 * This exists because the render queue and the assessment stream are NOT the
 * same sequence. `PhraseManager` renders a circular buffer that may contain
 * prefetched phrases and injected rest phrases; only ordinary played phrases
 * are assessments. PAD is counted in assessment phrases, so threading a verdict
 * to "the phrase after next" must ignore any rest phrases in between.
 *
 * Per the engineering spec: "Do not implement PAD as '+PAD positions in the
 * rendered queue.' Maintain the assessment stream as its own indexed sequence;
 * rest phrases are interleaved for playback but are not members of it."
 *
 * Indices are 1-based and contiguous. Rest phrases are recorded (so the
 * controller can recognise them on completion) but consume no index.
 */

import { Verdict } from './complexityModel.types';

/** How a phrase participates in complexity adjustment. */
export enum PhraseKind {
  /** An ordinary phrase: gets an index, a verdict, and moves the band. */
  Assessment = 'assessment',
  /** An injected rest phrase: inert. No index, no verdict, no PAD slot. */
  Rest = 'rest',
}

/** A phrase registered with the stream. */
export interface StreamEntry {
  uuid: string;
  kind: PhraseKind;
  /** 1-based assessment index. `undefined` for rest phrases. */
  idx?: number;
  /** Verdict once graded. `undefined` until then, always `undefined` for rest. */
  verdict?: Verdict;
  /** Complexity error tally that produced the verdict (red 1.0 + yellow 0.5). */
  errorTotal?: number;
  /** Band this phrase was actually drawn from, recorded at plan time.
   *
   *  The trajectory can be recomputed from the verdicts at any point, but only
   *  for assessment indices — and only forwards. Consumers that need "what band
   *  was the phrase now playing drawn from?" (the chevron CAT gate and the
   *  header progress bar) must read it back per phrase, including for rest
   *  phrases, which have no index at all. Hence it is stored, not derived. */
  band?: Readonly<{ crMin: number; crMax: number }>;
}

export class AssessmentStream {
  private readonly entries: StreamEntry[] = [];
  private readonly byUuid = new Map<string, StreamEntry>();
  /** Verdicts in assessment order; `verdicts[i]` belongs to index `i + 1`. */
  private readonly verdicts: Verdict[] = [];
  private nextIdx = 1;

  /**
   * Register a phrase as it enters playback.
   *
   * @returns the assigned 1-based assessment index, or `undefined` for a rest
   *          phrase (which deliberately consumes no index).
   * @throws if the uuid is already registered — double-registration would
   *         corrupt PAD threading, so it must be loud rather than silent.
   */
  register(
    uuid: string,
    kind: PhraseKind,
    band?: Readonly<{ crMin: number; crMax: number }>,
  ): number | undefined {
    if (this.byUuid.has(uuid)) {
      throw new Error(`AssessmentStream: phrase ${uuid} registered twice`);
    }

    const entry: StreamEntry =
      kind === PhraseKind.Assessment
        ? { uuid, kind, idx: this.nextIdx++, band }
        : { uuid, kind, band };

    this.entries.push(entry);
    this.byUuid.set(uuid, entry);
    return entry.idx;
  }

  /**
   * Record the outcome of a graded assessment phrase.
   *
   * @throws if the phrase is unknown, is a rest phrase, or was already graded.
   */
  grade(uuid: string, verdict: Verdict, errorTotal: number): void {
    const entry = this.byUuid.get(uuid);
    if (entry === undefined) {
      throw new Error(`AssessmentStream: cannot grade unregistered phrase ${uuid}`);
    }
    if (entry.kind === PhraseKind.Rest) {
      throw new Error(`AssessmentStream: rest phrase ${uuid} must never receive a verdict`);
    }
    if (entry.verdict !== undefined) {
      throw new Error(`AssessmentStream: phrase ${uuid} graded twice`);
    }
    if (entry.idx !== this.verdicts.length + 1) {
      throw new Error(
        `AssessmentStream: out-of-order grade for ${uuid} — expected index ` +
          `${this.verdicts.length + 1}, got ${String(entry.idx)}`,
      );
    }

    entry.verdict = verdict;
    entry.errorTotal = errorTotal;
    this.verdicts.push(verdict);
  }

  /** Verdicts in assessment order — the complexity model's input. */
  getVerdicts(): readonly Verdict[] {
    return this.verdicts;
  }

  /** Highest assessment index graded so far (0 when none). */
  getGradedCount(): number {
    return this.verdicts.length;
  }

  /** Assessment indices assigned so far, graded or not. */
  getRegisteredCount(): number {
    return this.nextIdx - 1;
  }

  /**
   * The index the next assessment phrase will receive. This is the index whose
   * band the request path must resolve.
   */
  getNextIdx(): number {
    return this.nextIdx;
  }

  /** Assessment phrases played since the last rest phrase (or session start). */
  getAssessmentsSinceLastRest(): number {
    let count = 0;
    for (let i = this.entries.length - 1; i >= 0; i -= 1) {
      if (this.entries[i].kind === PhraseKind.Rest) break;
      count += 1;
    }
    return count;
  }

  isRest(uuid: string): boolean {
    return this.byUuid.get(uuid)?.kind === PhraseKind.Rest;
  }

  getEntry(uuid: string): Readonly<StreamEntry> | undefined {
    return this.byUuid.get(uuid);
  }

  /** All entries in playback order, rest phrases included. For telemetry. */
  getEntries(): readonly Readonly<StreamEntry>[] {
    return this.entries;
  }

  /** Clear all state — used on tier change, where the scene is reused. */
  reset(): void {
    this.entries.length = 0;
    this.verdicts.length = 0;
    this.byUuid.clear();
    this.nextIdx = 1;
  }
}
